import React, { useRef, useState, useEffect } from "react";

import bildDhlStation from "./assets/dhl-station.jpg";
import bildNeussHBF from "./assets/neuss-hbf.jpg";
import bildReuschenbergKiosk from "./assets/reuschenberg-kiosk.jpg";
import bildSonisKiosk from "./assets/sonis-kiosk.jpg";
import demoBild from "./assets/demo.jpeg";

import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";

// --- Data: ECHTE VOM KUNDEN ---
const locations = [
  {
    position: [51.20159485642385, 6.6872048576715715],
    name: "DHL Station Neuss",
    address: "Krefelder Straße 68, 41460 Neuss",
    desc: "Maximale Reichweite: Straßenbahn, Busse, stehender Verkehr an der Ampel.",
    image: bildDhlStation,
  },
  {
    position: [51.201, 6.687],
    name: "Neuss HBF",
    address: "Hauptbahnhof",
    desc: "Verkehrsdrehkreuz der Stadt: Permanente Frequenz durch Pendler.",
    image: bildNeussHBF,
  },
  {
    position: [51.1785, 6.695],
    name: "Reuschenberg Kiosk",
    address: "Finkenstraße 6, 41466 Neuss",
    desc: "Starker Pendler-Spot: Autofahrer stoppen hier täglich.",
    image: bildReuschenbergKiosk,
  },
  {
    position: [51.192, 6.678],
    name: "Sonis Kiosk",
    address: "Gladbacher Straße, 41462 Neuss",
    desc: "Idealer Standort für Alltagswerbung: Hohe Frequenz.",
    image: bildSonisKiosk,
  },
];

const infoCards = [
  {
    id: "marke",
    title: "Markenbekanntheit",
    desc: "Wir machen deine Marke und deine Botschaft dort bekannt, wo es zählt. Ob du Mitarbeiter suchst, neue Angebote bewerben möchtest oder einfach Präsenz zeigen willst – wir erstellen Spots, die perfekt auf unsere Bildschirme zugeschnitten sind.",
    price: "Individual",
    icon: "★",
    colSpan: "md:col-span-8",
    bg: "bg-blue-600 text-white",
  },
  {
    id: "samsung",
    title: "Samsung OMN Displays",
    desc: "Brillante Farben, hohe Helligkeit und gestochen scharfe Inhalte sorgen dafür, dass deine Botschaft sofort ins Auge fällt – selbst bei Tageslicht.",
    price: "High-Tech",
    icon: "⚡",
    colSpan: "md:col-span-4",
    bg: "bg-gray-100 text-slate-900",
  },
  {
    id: "lokal",
    title: "Lokaler Partner",
    desc: "Wir sind der Partner für lokale und mittelständische Unternehmen. Unsere Werbeflächen bringen deine Botschaft an zehn stark frequentierte Standorte – direkt in den Alltag deiner Zielgruppe.",
    price: "Neuss-Exklusiv",
    icon: "🤝",
    colSpan: "md:col-span-4",
    bg: "bg-white border border-gray-200 text-slate-900",
  },
  {
    id: "sichtbar",
    title: "Sichtbarkeit & Service",
    desc: "Qualität allein reicht nicht aus, wenn sie niemand sieht. Wir helfen dir dabei, dein Unternehmen sichtbar zu machen. Ausstrahlung und Erstellung von Werbespots aus einer Hand.",
    price: "Full Service",
    icon: "👁",
    colSpan: "md:col-span-8",
    bg: "bg-slate-900 text-white",
  },
];

// --- Components ---

// ─── LogoSlot ─────────────────────────────────────────────────────────────────
const LogoSlot = ({ logo, visible }) => (
  <div className="flex items-center justify-center p-4 h-44">
    <img
      src={logo}
      alt="Partner Logo"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "scale(1)" : "scale(0.95)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
        width: "100%",
        height: "100%",
        objectFit: "contain",
      }}
    />
  </div>
);

// ─── PartnerSection ───────────────────────────────────────────────────────────
const PartnerSection = () => {
  let allLogos = [];
  try {
    const importAll = (r) => r.keys().map(r);
    allLogos = importAll(require.context("./assets/logos", false, /\.svg$/));
  } catch {
    // Kein Ordner vorhanden → Sektion wird ausgeblendet
  }

  const SLOT_COUNT = Math.min(10, allLogos.length);
  const CYCLE_INTERVAL = 10000; // 10 Sekunden
  const FADE_DURATION = 500; // 0.5s Übergang

  // Hooks IMMER vor jedem early return
  const [indices, setIndices] = useState(() =>
    Array.from(
      { length: SLOT_COUNT },
      (_, i) => i % Math.max(allLogos.length, 1)
    )
  );
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (allLogos.length === 0) return;

    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndices((prev) =>
          prev.map((idx) => (idx + SLOT_COUNT) % allLogos.length)
        );
        setVisible(true);
      }, FADE_DURATION);
    }, CYCLE_INTERVAL);

    return () => clearInterval(interval);
  }, [allLogos.length]);

  // Early return NACH den Hooks
  if (allLogos.length === 0) return null;

  return (
    <section className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <h3 className="text-5xl font-bold text-slate-900">
            Wer uns bereits vertraut.
          </h3>
        </div>

        {/* Logo-Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 items-center">
          {indices.map((logoIdx, slotIdx) => (
            <LogoSlot
              key={slotIdx}
              logo={allLogos[logoIdx]}
              visible={visible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const NoiseOverlay = () => (
  <div className="pointer-events-none fixed inset-0 z-50 h-full w-full opacity-[0.04] mix-blend-overlay">
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <filter id="noiseFilter">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.8"
          numOctaves="3"
          stitchTiles="stitch"
        />
      </filter>
      <rect width="100%" height="100%" filter="url(#noiseFilter)" />
    </svg>
  </div>
);

const Marquee = () => {
  const items = [
    "Standort Neuss",
    "DHL Station",
    "Sam’s Kiosk",
    "Further Kiosk",
    "Neuss HBF",
    "Reuschenberg",
    "Zolltor",
  ];

  return (
    <div className="bg-slate-900 text-white py-6 overflow-hidden border-y border-white/10 relative z-10">
      <div className="animate-marquee flex gap-12 items-center whitespace-nowrap">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="flex gap-12 items-center">
            {items.map((item, idx) => (
              <span
                key={idx}
                className="text-sm font-mono font-bold uppercase tracking-widest opacity-80 flex items-center gap-4"
              >
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                {item}
              </span>
            ))}
          </div>
        ))}
      </div>
      <style>{`
        .animate-marquee { animation: marquee 40s linear infinite; width: max-content; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      `}</style>
    </div>
  );
};

const SectionHeader = ({ subtitle, title, dark = false }) => (
  <div className="mb-5">
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6 ${
        dark ? "bg-white/10 text-blue-300" : "bg-blue-50 text-blue-700"
      }`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
      {subtitle}
    </div>
    <h2
      className={`text-5xl md:text-6xl font-bold tracking-tighter leading-[0.95] ${
        dark ? "text-white" : "text-slate-900"
      }`}
    >
      {title}
    </h2>
  </div>
);

const LocationCard = ({ loc }) => (
  <div className="min-w-[320px] md:min-w-[420px] snap-center group cursor-pointer relative h-[550px] overflow-hidden rounded-[3rem] shadow-2xl bg-slate-100">
    {/* Image mit effekt */}
    <img
      src={loc.image}
      alt={loc.name}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.5s] cubic-bezier(0.4, 0, 0.2, 1) group-hover:scale-110"
    />

    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/20 to-transparent opacity-80 group-hover:opacity-90 transition-all duration-500"></div>

    {/* Content Container */}
    <div className="absolute inset-0 p-8 flex flex-col justify-end">
      <div className="relative z-10 transition-transform duration-700 ease-out">
        <div className="h-[2px] w-8 bg-white mb-6 group-hover:w-24 transition-all duration-700 ease-in-out"></div>

        <div className="text-[11px] font-black text-white uppercase tracking-[0.2em] mb-3">
          {loc.address}
        </div>

        <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tighter leading-none font-display">
          {loc.name}
        </h3>

        <div className="transition-all duration-700 ease-in-out">
          <p className="text-base text-slate-300 font-light leading-relaxed mb-6">
            {loc.desc}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ContactForm = () => {
  const form = useRef();
  const [status, setStatus] = useState("idle");

  const sendEmail = (e) => {
    e.preventDefault();
    setStatus("submitting");

    emailjs
      .sendForm("service_0cfq0zj", "template_9bhk9ps", form.current, {
        publicKey: "YbEBMmvJpad-4JPjL",
      })
      .then(
        () => {
          setStatus("success");
          e.target.reset();
          setTimeout(() => setStatus("idle"), 5000);
        },
        (error) => {
          console.log("FAILED...", error.text);
          setStatus("idle");
          alert("Fehler beim Senden. Bitte versuchen Sie es erneut.");
        }
      );
  };

  return (
    <form
      ref={form}
      onSubmit={sendEmail}
      className="space-y-6 mt-8 bg-white p-6 md:p-12 rounded-[3rem] shadow-[0_32px_64px_-15px_rgba(0,0,0,0.1)] relative z-20 border border-slate-300"
    >
      <div className="grid md:grid-cols-2 gap-6">
        <div className="group">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-3 ml-2 group-focus-within:text-blue-600 transition-colors">
            Name
          </label>
          <input
            type="text"
            name="user_name"
            required
            className="w-full bg-slate-50 border-2 border-transparent rounded-2xl px-6 py-4 focus:outline-none focus:border-blue-500/20 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 font-medium text-slate-900 placeholder:text-slate-300"
            placeholder="Max Mustermann"
          />
        </div>
        <div className="group">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-3 ml-2 group-focus-within:text-blue-600 transition-colors">
            Email
          </label>
          <input
            type="email"
            name="user_email"
            required
            className="w-full bg-slate-50 border-2 border-transparent rounded-2xl px-6 py-4 focus:outline-none focus:border-blue-500/20 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 font-medium text-slate-900 placeholder:text-slate-300"
            placeholder="hallo@firma.de"
          />
        </div>
      </div>

      <div className="group">
        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.15em] mb-3 ml-2 group-focus-within:text-blue-600 transition-colors">
          Nachricht
        </label>
        <textarea
          name="message"
          rows="4"
          required
          className="w-full bg-slate-50 border-2 border-transparent rounded-3xl px-6 py-4 focus:outline-none focus:border-blue-500/20 focus:bg-white focus:ring-4 focus:ring-blue-500/5 transition-all duration-300 font-medium text-slate-900 placeholder:text-slate-300 resize-none"
          placeholder="Erzähl uns von deinem Projekt..."
        ></textarea>
      </div>

      <button
        type="submit"
        disabled={status === "success" || status === "submitting"}
        className={`w-full font-bold py-5 rounded-2xl transition-all duration-500 flex items-center justify-center gap-3 mt-4 text-lg
            ${
              status === "success"
                ? "bg-green-500 text-white shadow-lg shadow-green-500/20"
                : "bg-slate-900 text-white hover:bg-blue-600 hover:-translate-y-1 shadow-xl hover:shadow-blue-600/20 active:scale-95"
            }
          `}
      >
        {status === "submitting" ? (
          <span className="animate-pulse">Sende...</span>
        ) : status === "success" ? (
          <>Anfrage gesendet ✓</>
        ) : (
          <>
            Jetzt anfragen <span className="text-xl">→</span>
          </>
        )}
      </button>
    </form>
  );
};

// --- Main Page ---

const Home = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-[#f8f9fa] text-slate-900 font-sans antialiased selection:bg-blue-500 selection:text-white relative">
      <NoiseOverlay />
      <style>{`
        body { font-family: 'Inter', sans-serif; }
        h1, h2, h3, .font-display { font-family: 'Space Grotesk', sans-serif; }
      `}</style>
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex flex-col justify-center pt-24 pb-12 overflow-hidden bg-white rounded-b-[3rem] md:rounded-b-[5rem] shadow-sm z-10">
        {/* Background  */}
        <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-blue-50 via-transparent to-transparent opacity-60 pointer-events-none"></div>
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-[80px] opacity-20 animate-pulse"></div>

        <div className="max-w-[1400px] mx-auto w-full px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7 flex flex-col items-start">
            <h1 className="text-6xl md:text-[5.5rem] lg:text-[7rem] leading-[0.9] font-bold tracking-tighter text-slate-900 mb-8">
              Sichtbarkeit <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-blue-700 italic pr-4">
                wo es zählt.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-500 font-light leading-relaxed max-w-xl mb-12">
              Jede Botschaft kommt an.{" "}
              <strong className="text-slate-900 font-medium">
                Lokal, digital und unübersehbar.
              </strong>{" "}
              Wir bringen deine Marke an die Hotspots von Neuss.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate("/standorte")}
                className="bg-slate-900 text-white px-8 py-4 rounded-full font-bold hover:bg-blue-600 transition-colors duration-300"
              >
                Standorte ansehen
              </button>
              <button
                onClick={() => navigate("/kontakt")}
                className="bg-white border border-slate-200 text-slate-900 px-8 py-4 rounded-full font-bold hover:bg-slate-50 transition-colors duration-300"
              >
                Preise anfragen
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 relative h-[600px] flex items-center justify-center lg:justify-end">
            <div className="absolute inset-0 bg-slate-100 rounded-full rotate-12 scale-75 blur-3xl opacity-50"></div>

            {/* Phone container */}
            <div className="relative w-[300px] aspect-[9/16] bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl rotate-[-6deg] hover:rotate-0 transition-transform duration-700 ease-out border-4 border-slate-800">
              <div className="w-full h-full bg-black rounded-[2rem] overflow-hidden relative">
                <iframe
                  className="w-full h-full object-cover scale-[1.02]"
                  src="https://www.youtube.com/embed/vdgOUXdThO8?autoplay=0&loop=1&playlist=vdgOUXdThO8&controls=0&showinfo=0"
                  title="Showreel"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                ></iframe>

                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Badge */}
            <div className="absolute bottom-20 -left-6 md:left-10 bg-white p-6 rounded-2xl shadow-xl max-w-[200px] animate-bounce-slow border border-slate-100 hidden sm:block">
              <div className="text-4xl font-bold text-blue-600 mb-1">10+</div>
              <div className="text-sm font-medium text-slate-500 leading-tight">
                High-Traffic Standorte in Neuss
              </div>
            </div>
          </div>
        </div>
      </section>
      <Marquee />

      <section className="py-32 px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="grid md:grid-cols-2 gap-16 md:gap-32 items-center">
          <div className="relative order-2 md:order-1">
            <div className="relative z-10 overflow-hidden rounded-[1rem] md:rounded-[3rem]">
              <img
                src={demoBild}
                alt="Agency"
                loading="lazy"
                className="w-full transition-all duration-700"
              />
            </div>

            <div className="absolute -top-10 -left-10 w-full h-full border-2 border-blue-600/30 rounded-[3rem] -z-0 hidden md:block"></div>
          </div>

          <div className="order-1 md:order-2">
            <SectionHeader subtitle="Mission" title="Wir sind Neuss." />
            <div className="space-y-8 text-lg md:text-xl text-slate-600 font-light leading-relaxed">
              <p>
                <strong className="text-slate-900 font-semibold underline decoration-blue-400 decoration-2 underline-offset-4">
                  Außenwerbung muss nicht kompliziert sein.
                </strong>{" "}
                Wir sind der Partner für lokale Unternehmen, die gezielt
                Aufmerksamkeit erzeugen wollen.
              </p>
              <p>
                Unsere Werbeflächen bringen deine Botschaft an zehn stark
                frequentierte Standorte – direkt in den Alltag deiner
                Zielgruppe. Bezahlbar, wirkungsvoll und auf den Punkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- PARTNER SEKTION --- */}
      <PartnerSection />

      {/* --- KUNDENSTIMME / REVIEW --- */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader
            subtitle="Kundenstimme"
            title="Das sagen unsere Kunden."
          />
          <div className="max-w-4xl mt-12 mx-auto">
            <div className="relative w-full aspect-video overflow-hidden rounded-[1.5rem] md:rounded-[3rem] shadow-2xl bg-black">
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube-nocookie.com/embed/I1oh-af2c-g?rel=0&modestbranding=1"
                title="Kundenstimme"
                loading="lazy"
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <section
        id="services"
        className="py-20 px-6 md:px-12 bg-white border-y border-slate-100"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <SectionHeader
              subtitle="Leistungen"
              title="Alles was du brauchst."
            />
            <p className="max-w-md text-slate-500 mb-8 md:text-right">
              Von der reinen Sendezeit bis zur kompletten Produktion deines
              Spots. Wir schnüren das Paket, das zu dir passt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(300px,auto)]">
            {infoCards.map((card) => (
              <div
                key={card.id}
                className={`${card.colSpan} ${card.bg} relative p-8 md:p-12 rounded-[2.5rem] flex flex-col justify-between overflow-hidden group transition-all duration-500 hover:shadow-2xl hover:-translate-y-1`}
              >
                <div className="absolute top-0 right-0 p-8 opacity-10 md:opacity-20 transition-transform duration-700 group-hover:scale-150 group-hover:rotate-12">
                  <span className="text-9xl">{card.icon}</span>
                </div>

                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-6">
                    <span className="inline-block px-3 py-1 rounded-full border border-current opacity-60 text-xs font-bold uppercase tracking-wider">
                      {card.price}
                    </span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight leading-none">
                    {card.title}
                  </h3>
                  <p className="opacity-80 text-lg leading-relaxed max-w-xl">
                    {card.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* LOCATIONS */}
      <section id="locations" className="py-24 bg-slate-50 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-12 flex items-end justify-between">
          <SectionHeader subtitle="Netzwerk" title="Unser Neuss-Netz." />
        </div>

        <div className="relative w-full overflow-visible">
          <div
            className="flex gap-6 overflow-x-auto px-6 md:px-12 pb-12 snap-x snap-mandatory scrollbar-hide"
            style={{ scrollbarWidth: "none" }}
          >
            {locations.map((loc, idx) => (
              <LocationCard key={idx} loc={loc} />
            ))}

            <div
              className="min-w-[300px] snap-center flex flex-col items-center justify-center bg-white border-2 border-dashed border-slate-200 rounded-[2rem] text-center p-8 cursor-pointer hover:border-blue-500 hover:bg-blue-50/30 group transition-all duration-300"
              onClick={() => navigate("/standorte")}
            >
              {/* Animiertes Icon beim Hover */}
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-4xl mb-4 group-hover:scale-110 group-hover:bg-blue-100 transition-all">
                📍
              </div>

              <h3 className="text-xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                Mehr Ansehen
              </h3>

              <p className="text-sm text-slate-500 mt-2">
                Wir haben noch viel mehr Standorte in Neuss!
                <span className="inline-block ml-1 group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CONTACT  */}
      <section
        id="contact"
        className="relative py-24 px-6 md:px-12 bg-white overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600 rounded-full mix-blend-screen filter blur-[150px] opacity-20 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600 rounded-full mix-blend-screen filter blur-[120px] opacity-10 pointer-events-none"></div>

        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-20 items-center relative z-10">
          <div>
            <SectionHeader
              subtitle="Kontakt"
              title="Lass uns reden."
              dark={false}
            />
            <p className="text-xl text-slate-600 font-light mb-12 max-w-md leading-relaxed">
              Deine Marke gehört auf die Straße, nicht in die Schublade. Schreib
              uns für ein unverbindliches Angebot.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div>
                  <h4 className="font-bold text-lg mb-1">Email</h4>
                  <p className="text-slate-400">info@showoffnrw.de</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div>
                  <h4 className="font-bold text-lg mb-1">Preise</h4>
                  <p className="text-slate-400">Auf Anfrage</p>
                </div>
              </div>
            </div>
          </div>

          <ContactForm />
        </div>
      </section>
      <style>{`
         @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
         }
         .animate-bounce-slow { animation: bounce-slow 3s infinite ease-in-out; }
      `}</style>
    </div>
  );
};

export default Home;
